<template>
  <transition name="notification-slide">
    <div :class="['notification', `notification-${type}`]" v-if="show">
      <div class="notification-content">
        <div class="notification-icon">
          <span v-if="type === 'error'">⚠️</span>
          <span v-else-if="type === 'success'">✅</span>
          <span v-else-if="type === 'warning'">⚠️</span>
          <span v-else>ℹ️</span>
        </div>
        <div class="notification-message">{{ message }}</div>
      </div>
      <button class="notification-close" @click="closeNotification">×</button>
    </div>
  </transition>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, onMounted, onBeforeUnmount } from 'vue'

// Define props interface
interface Props {
  message: string
  type?: 'info' | 'success' | 'warning' | 'error'
  duration?: number
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  duration: 5000
})

// Define emits interface
interface Emits {
  close: []
}

const emit = defineEmits<Emits>()

// Reactive data
const show = ref(true)
const timer = ref<NodeJS.Timeout | null>(null)

// Methods
const closeNotification = (): void => {
  show.value = false
  setTimeout(() => {
    emit('close')
  }, 300) // Wait for animation to complete
}

// Lifecycle hooks
onMounted(() => {
  if (props.duration > 0) {
    timer.value = setTimeout(() => {
      closeNotification()
    }, props.duration)
  }
})

onBeforeUnmount(() => {
  if (timer.value) {
    clearTimeout(timer.value)
  }
})
</script>

<style scoped>
.notification {
  position: fixed;
  top: var(--space-lg);
  right: var(--space-lg);
  z-index: 9999;
  width: 350px;
  padding: var(--space-md);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.notification-content {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  flex: 1;
}

.notification-icon {
  font-size: var(--font-size-xl);
  flex-shrink: 0;
}

.notification-message {
  font-size: var(--font-size-sm);
  line-height: 1.5;
  word-break: break-word;
}

.notification-close {
  background: transparent;
  border: none;
  font-size: var(--font-size-xl);
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color var(--transition-fast) var(--easing);
  padding: 0;
  margin-left: var(--space-sm);
}

.notification-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.notification-info {
  background-color: var(--info);
  color: white;
}

.notification-success {
  background-color: var(--success);
  color: white;
}

.notification-warning {
  background-color: var(--warning);
  color: var(--neutral-900);
}

.notification-error {
  background-color: var(--error);
  color: white;
}

.notification-slide-enter-active,
.notification-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.notification-slide-enter-from,
.notification-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
